/**
===================================================
Module: frontend.src.utils.pdfThumbnail.tsx
Description: PDF thumbnail generation utilities
Owner: Kawada Technosystem Co Ltd
All Rights Reserved
This script is proprietary and confidential.
Unauthorized copying, distribution, or modification of this
file, via any medium, is strictly prohibited.
===================================================
*/

import React, { useState, useRef, useEffect } from 'react';
import { loadPdfDocument, PDF_CONFIG } from './pdfConfig';

export interface ThumbnailResult {
    dataUrl: string;
    blob: Blob;
    contentType: string;
}

export const generatePlaceholderThumbnail = async (
    width: number = 200,
    height: number = 280
): Promise<ThumbnailResult> => {
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    
    if (!ctx) {
        throw new Error('Failed to get canvas context');
    }
    
    // Draw background
    ctx.fillStyle = "#f9fafb";
    ctx.fillRect(0, 0, width, height);
    
    // Draw border
    ctx.strokeStyle = "#e5e7eb";
    ctx.lineWidth = 2;
    ctx.strokeRect(0, 0, width, height);
    
    // Calculate center and icon size
    const centerX = width / 2;
    const centerY = height / 2 - 20;
    const iconSize = Math.min(width, height) * 0.3;
    
    // Draw PDF file icon (simplified document shape)
    ctx.save();
    ctx.translate(centerX, centerY);
    
    // Document outline
    ctx.fillStyle = "#9ca3af";
    ctx.strokeStyle = "#6b7280";
    ctx.lineWidth = 2;
    
    const docWidth = iconSize * 0.7;
    const docHeight = iconSize;
    const foldSize = iconSize * 0.2;
    
    ctx.beginPath();
    ctx.moveTo(-docWidth / 2, -docHeight / 2);
    ctx.lineTo(docWidth / 2 - foldSize, -docHeight / 2);
    ctx.lineTo(docWidth / 2, -docHeight / 2 + foldSize);
    ctx.lineTo(docWidth / 2, docHeight / 2);
    ctx.lineTo(-docWidth / 2, docHeight / 2);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    
    // Fold corner
    ctx.beginPath();
    ctx.moveTo(docWidth / 2 - foldSize, -docHeight / 2);
    ctx.lineTo(docWidth / 2 - foldSize, -docHeight / 2 + foldSize);
    ctx.lineTo(docWidth / 2, -docHeight / 2 + foldSize);
    ctx.strokeStyle = "#6b7280";
    ctx.stroke();
    
    // Lines on document to indicate text
    ctx.strokeStyle = "#ffffff";
    ctx.lineWidth = 2;
    const lineSpacing = iconSize * 0.12;
    const lineWidth = docWidth * 0.6;
    const startY = -docHeight / 4;
    
    for (let i = 0; i < 4; i++) {
        const y = startY + (i * lineSpacing);
        ctx.beginPath();
        ctx.moveTo(-lineWidth / 2, y);
        ctx.lineTo(lineWidth / 2, y);
        ctx.stroke();
    }
    
    ctx.restore();
    
    // Draw "PDF" text below icon
    ctx.fillStyle = "#6b7280";
    ctx.font = `bold ${iconSize * 0.35}px Arial`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText("PDF", centerX, centerY + iconSize * 0.8);

    const dataUrl = canvas.toDataURL("image/png");
    
    return new Promise<ThumbnailResult>((resolve, reject) => {
        canvas.toBlob((blob) => {
            if (blob) {
                resolve({
                    dataUrl,
                    blob,
                    contentType: 'image/png',
                });
            } else {
                reject(new Error('Failed to create blob from placeholder canvas'));
            }
        }, 'image/png');
    });
};

interface PdfThumbnailGeneratorProps {
    file: File;
    onThumbnailGenerated: (result: ThumbnailResult) => void;
    onError: (error: string) => void;
    maxWidth?: number;
    maxHeight?: number;
}

// Simple PDF thumbnail generator using PDF.js
export const generatePdfThumbnail = async (
    file: File,
    maxWidth: number = 200,
    maxHeight: number = 200
): Promise<ThumbnailResult> => {
    try {
        const arrayBuffer = await file.arrayBuffer();
        // Use the configured PDF loader with CMap support
        const pdf = await loadPdfDocument(arrayBuffer);
        const page = await pdf.getPage(1); // Get first page

        const viewport = page.getViewport({ scale: 1 });
        
        // Calculate scale to fit within max dimensions
        const scaleX = maxWidth / viewport.width;
        const scaleY = maxHeight / viewport.height;
        const scale = Math.min(scaleX, scaleY);
        
        const scaledViewport = page.getViewport({ scale });

        // Create canvas
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d')!;
        canvas.width = scaledViewport.width;
        canvas.height = scaledViewport.height;

        // Render PDF page to canvas
        const renderContext = {
            canvasContext: context,
            viewport: scaledViewport,
        };

        await page.render(renderContext).promise;

        // Convert to blob and data URL
        const dataUrl = canvas.toDataURL('image/jpeg', 0.9);
        
        return new Promise<ThumbnailResult>((resolve, reject) => {
            canvas.toBlob((blob) => {
                if (blob) {
                    resolve({
                        dataUrl,
                        blob,
                        contentType: 'image/jpeg',
                    });
                } else {
                    reject(new Error('Failed to create blob from canvas'));
                }
            }, 'image/jpeg', 0.9);
        });

    } catch (error) {
        // If PDF extraction fails, fall back to placeholder thumbnail
        return generatePlaceholderThumbnail(maxWidth, maxHeight);
    }
};

// React component version for UI use
const PdfThumbnailGenerator: React.FC<PdfThumbnailGeneratorProps> = ({
    file,
    onThumbnailGenerated,
    onError,
    maxWidth = 200,
    maxHeight = 200,
}) => {
    const [isGenerating, setIsGenerating] = useState(false);
    const hasGenerated = useRef(false);

    useEffect(() => {
        if (hasGenerated.current) return;
        hasGenerated.current = true;

        const generate = async () => {
            setIsGenerating(true);
            try {
                const result = await generatePdfThumbnail(file, maxWidth, maxHeight);
                onThumbnailGenerated(result);
            } catch (error) {
                onError(error instanceof Error ? error.message : 'Unknown error');
            } finally {
                setIsGenerating(false);
            }
        };

        generate();
    }, [file, onThumbnailGenerated, onError, maxWidth, maxHeight]);

    return (
        <div style={{ padding: '20px', textAlign: 'center' }}>
            {isGenerating ? (
                <div>Generating PDF thumbnail...</div>
            ) : (
                <div>Thumbnail generation complete</div>
            )}
        </div>
    );
};

// Simple PDF viewer component (optional)
export const SimplePdfViewer: React.FC<{ 
    pdfUrl: string; 
    width?: string; 
    height?: string; 
}> = ({ 
    pdfUrl, 
    width = '100%', 
    height = '600px' 
}) => {
    return (
        <iframe
            src={pdfUrl}
            width={width}
            height={height}
            style={{
                border: '1px solid #ddd',
                borderRadius: '4px',
            }}
            title="PDF Viewer"
        />
    );
};

// Hook for easy thumbnail generation in React components
export const usePdfThumbnail = () => {
    const [isGenerating, setIsGenerating] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const generateThumbnail = async (
        file: File,
        maxWidth?: number,
        maxHeight?: number
    ): Promise<ThumbnailResult | null> => {
        setIsGenerating(true);
        setError(null);

        try {
            const result = await generatePdfThumbnail(file, maxWidth, maxHeight);
            return result;
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Unknown error';
            setError(errorMessage);
            return null;
        } finally {
            setIsGenerating(false);
        }
    };

    return {
        generateThumbnail,
        isGenerating,
        error,
    };
};

export default PdfThumbnailGenerator;